import { useState, useEffect } from 'react';
import DropDown from '../components/DropDown';
import Scanner from '../components/Scanner';
import API_ROOT from '../config';
import { PrimaryButton, SecondaryButton } from '../components/Button';
import { useFetch } from './Launchpad';
import LicenseData from '../components/LicenseData';

function Activate({ tenant, onDone }) {
    const [slot, setSlot] = useState('');
    const [notes, setNotes] = useState('');
    const [location, setLocation] = useState('');
    const [qrCode, setQrCode] = useState('');
    const [licenseData, setLicenseData] = useState('');
    const [error, setError] = useState('');
    const [slots, setSlots] = useState(undefined);
    const { fetchWithAuth } = useFetch();

    const activate = async () => {
        const options = {
            method: 'POST',
            body: JSON.stringify(
                {
                    action: "activate",
                    identifier: slot,
                    request_base64: qrCode,
                    delay: 0,
                    developer: false,
                    additional_parameters: [
                        `Notes=${notes}`,
                        `Location=${location}`
                    ]
                }
            )
        };

        const response = await fetchWithAuth(`${API_ROOT}/v1/license/${tenant}`, options);

        if (response.status === 402) {
            setError(`This license slot does not match the cabinet's features.`);
            return
        }

        if (response.status === 403) {
            setError(`This license slot is not available.`);
            return
        }

        if (!response.ok) {
            let errorText = await response.text();
            const errorPrefix = 'Unable to activate cabinet: ';
            try {
                let errorData = JSON.parse(errorText);
                setError(errorPrefix + (errorData.Message ?? ""));
            }
            catch (e) {
                setError(errorPrefix + errorText);
            }
            console.error(errorPrefix, response);
            return;
        }

        if (response.status === 208) {
            setError('Cabinet already active');
        }
        const key = await response.json();
        setLicenseData(key);
    }

    // Fetch slots when the component mounts or tenant changes
    useEffect(() => {

        const getSlots = async () => {
            try {
                let url = `${API_ROOT}/slots/${tenant}`;
                if (qrCode) {
                    url += `?request=${encodeURIComponent(qrCode)}`;
                }
                const response = await fetchWithAuth(url);

                if (response.status === 403) {
                    setError('You do not have permission to view available licenses for this tenant.');
                    setSlots([]);
                    return;
                }
                if (response.status === 204) {
                    setSlots([]);
                    return;
                }
                if (!response.ok) {
                    setSlots([]);
                    setError('An error occurred while fetching available licenses: ' + response.statusText);
                    return;
                }

                const data = await response.json();
                setSlots(data);
            } catch (error) {
                console.error('Error fetching:', error);
            }
        }

        if (tenant) {
            getSlots();
        }
    }, [tenant, qrCode]);


    const clear = () => {
        setQrCode('');
        setLicenseData('');
        setError('');
    }

    const canActivate = tenant && slot && qrCode;

    if (slots === undefined) {
        return <div>Loading...</div>;
    }

    if (slots.length === 0) {

        return <div>
            <div>{error || 'No licenses available for this tenant.'}</div>
            <SecondaryButton onClick={onDone}>Back</SecondaryButton>
        </div>;
    }

    const licenseExists = !!licenseData;

    return (
        <div>

            {qrCode ? 'Code scanned successfully' : <div>
                Set the cabinet to audit mode and scan the QR code to activate it.
                <Scanner onScan={setQrCode} />
            </div>}

            {tenant && qrCode && <DropDown
                placeholder="Select a license type"
                label="License Slot:"
                options={slots}
                value={slot}
                disabled={licenseExists}
                idProperty='identifier'
                onChange={setSlot} />
            }

            {
                slot &&
                <>
                    <input
                        disabled={licenseExists}
                        type="text"
                        className="form-control my-2"
                        placeholder="Location"
                        value={location}
                        onChange={e => setLocation(e.target.value)}
                    />

                    <textarea
                        disabled={licenseExists}
                        className="form-control my-2"
                        placeholder="Notes"
                        value={notes}
                        onChange={e => setNotes(e.target.value)}
                    />
                </>
            }


            {!(error || licenseData) && <PrimaryButton disabled={!canActivate} onClick={activate}>Activate</PrimaryButton>}
            {licenseData && <div><LicenseData data={licenseData} /></div>}

            {(error || licenseData) && <SecondaryButton onClick={clear}>Register another cabinet</SecondaryButton>}
            <SecondaryButton onClick={onDone}>{licenseData ? "Done" : "Cancel"}</SecondaryButton>

            {error && <div className="alert alert-danger mt-2">{error}</div>}
        </div>
    );
}

export default Activate;